import type { Component } from '../../schema';
import type { SelectionsMap } from '../../state/selections';
import { effectiveSelections, type VisibilityRules } from '../../state/effectiveSelections';
import { collectCombinedImageLayers } from './layers';

/**
 * Render the current combined-image layers onto an offscreen canvas and
 * return the result as a `data:image/png;base64,…` URL.
 *
 * Returns `null` when:
 *  - the component doesn't use combined-image rendering,
 *  - no layers are selected (nothing to draw),
 *  - every layer image fails to load (e.g. all CORS-blocked).
 *
 * Cross-origin notes — mirrors legacy `app/webshop.js:finalizeAction`:
 * the layer URLs come from `cloud.configento.app` (or the legacy CDN),
 * and our backend rewrites them to absolute URLs in `enrichImageUrls`.
 * Each image is loaded with `crossOrigin="anonymous"` so the resulting
 * canvas isn't tainted; if a single image refuses CORS we skip it
 * rather than aborting the whole snapshot (legacy would just leave a
 * blank slot in that case).
 *
 * Sizing — we composite at the **natural** size of the largest layer,
 * matching legacy `webshop.js:81-91` (loop over `img.naturalWidth/Height`
 * to pick the canvas size). Each smaller layer is centred on the canvas.
 *
 * `rules` scrubs rule-removed picks out of the stack so the PNG attached to
 * the savebutton payload shows what the customer actually saw. Legacy builds
 * its layers from `option.getActiveChoice()` (combinedimage.js:64), which a
 * blacklist has already nulled.
 */
export async function snapshotCombinedImage(
    component: Component,
    selections: SelectionsMap,
    rules?: VisibilityRules,
): Promise<string | null> {
    if (component.combinedImage !== 'combined-product-image') return null;
    const effective = rules ? effectiveSelections(selections, rules) : selections;
    const layers = collectCombinedImageLayers(component, effective);
    if (layers.length === 0) return null;
    if (typeof document === 'undefined') return null;

    // Load every image in parallel. Skip individual failures rather than
    // failing the whole snapshot — partial composite is still useful.
    const loaded = await Promise.all(
        layers.map((layer) => loadImage(layer.value.image).catch(() => null)),
    );
    const images = loaded.filter((img): img is HTMLImageElement => img !== null);
    if (images.length === 0) return null;

    let width = 0;
    let height = 0;
    for (const img of images) {
        if (img.naturalWidth > width) width = img.naturalWidth;
        if (img.naturalHeight > height) height = img.naturalHeight;
    }
    // Defensive minimum — shouldn't happen for real images, but guards
    // against decoded-but-zero-sized SVGs.
    if (width === 0 || height === 0) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // White background matches legacy `drawRect({fillStyle:'#fff'})`. PDFs
    // and email clients render transparent PNGs against dark themes oddly,
    // so we always bake a neutral background in.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    for (const img of images) {
        // Centre each layer on the canvas — preserves the legacy layout
        // (`webshop.js:169-171`).
        const x = (width - img.naturalWidth) / 2;
        const y = (height - img.naturalHeight) / 2;
        ctx.drawImage(img, x, y);
    }

    try {
        return canvas.toDataURL('image/png');
    } catch {
        // SecurityError when a layer tainted the canvas. Bail rather than
        // shipping a half-broken snapshot.
        return null;
    }
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Image load failed: ${src}`));
        img.src = src;
    });
}
